import * as React from "react";
import { US_STATES } from "@volca/ui/components/ui/autocomplete";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from "@volca/ui/components/ui/combobox";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import {
  InputGroupAddon,
  InputGroupButton,
} from "@volca/ui/components/ui/input-group";
import { cn } from "@volca/ui/lib/utils";

export interface AddressValue {
  line_one: string;
  line_two: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

interface AddressInputProps {
  value?: Partial<AddressValue>;
  onChange?: (value: AddressValue) => void;
  required?: boolean;
  error?: string;
  className?: string;
  labels?: Omit<AddressValue, "country"> & { country?: string };
}

type UsState = (typeof US_STATES)[number];

const DEFAULT_LABELS = {
  line_one: "Address",
  line_two: "Company, C/O, Apt, Suite, Unit",
  city: "City",
  state: "State",
  zip_code: "ZIP",
};

const AddressLine = ({
  label,
  error,
  showErrorMessage = true,
  required,
  id,
  ...props
}: {
  label?: string;
  error?: string;
  showErrorMessage?: boolean;
} & React.ComponentProps<typeof Input>) => {
  const generatedId = React.useId();
  const inputId = id ?? generatedId;
  return (
    <Field data-invalid={Boolean(error) || undefined}>
      <FieldLabel htmlFor={inputId} className="gap-0.5">
        {label}
        {required && <span aria-hidden="true">*</span>}
      </FieldLabel>
      <Input
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        {...props}
      />
      {error && showErrorMessage && <FieldError>{error}</FieldError>}
    </Field>
  );
};

export const AddressInput = React.forwardRef<HTMLDivElement, AddressInputProps>(
  (
    {
      value = {},
      onChange,
      labels = DEFAULT_LABELS,
      required = false,
      error,
      className,
    },
    ref,
  ) => {
    const stateId = React.useId();
    const errorId = React.useId();
    const hasAnyContent = Object.entries(value).some(
      ([key, part]) => key !== "country" && Boolean(part),
    );
    const isRequired = required || hasAnyContent;

    const requiredParts = ["line_one", "city", "state", "zip_code"] as const;
    const emptyParts = requiredParts.filter((part) => !value[part]);
    const invalidParts = new Set<string>(
      error ? (emptyParts.length ? emptyParts : requiredParts) : [],
    );
    const partError = (part: (typeof requiredParts)[number]) =>
      invalidParts.has(part)
        ? { error, showErrorMessage: false, "aria-describedby": errorId }
        : { error: undefined };

    const update = (part: Partial<AddressValue>) =>
      onChange?.({ ...(value as AddressValue), ...part });

    const selectedState =
      US_STATES.find((state) => state.value === value.state) ?? null;

    return (
      <FieldGroup ref={ref} className={cn("gap-3", className)}>
        <AddressLine
          label={labels.line_one}
          {...partError("line_one")}
          required={isRequired}
          value={value.line_one || ""}
          onChange={(e) => update({ line_one: e.target.value })}
        />
        <AddressLine
          label={labels.line_two}
          value={value.line_two || ""}
          onChange={(e) => update({ line_two: e.target.value })}
        />
        <AddressLine
          label={labels.city}
          {...partError("city")}
          required={isRequired}
          value={value.city || ""}
          onChange={(e) => update({ city: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field data-invalid={invalidParts.has("state") || undefined}>
            <FieldLabel htmlFor={stateId} className="gap-0.5">
              {labels.state}
              {isRequired && <span aria-hidden="true">*</span>}
            </FieldLabel>
            <Combobox<UsState>
              items={US_STATES}
              value={selectedState}
              onValueChange={(next) => update({ state: next?.value ?? "" })}
              itemToStringLabel={(state) => state.label}
            >
              {/* The preset's trigger has no name, so this one replaces it. */}
              <ComboboxInput
                id={stateId}
                required={isRequired}
                aria-invalid={invalidParts.has("state") || undefined}
                aria-describedby={
                  invalidParts.has("state") ? errorId : undefined
                }
                showTrigger={false}
                className="w-full [&>[data-slot=input-group-addon]:empty]:hidden"
              >
                <InputGroupAddon align="inline-end">
                  <InputGroupButton
                    size="icon-xs"
                    variant="ghost"
                    render={<ComboboxTrigger />}
                    aria-label={`Show ${labels.state.toLowerCase()} options`}
                    className="data-pressed:bg-transparent"
                  />
                </InputGroupAddon>
              </ComboboxInput>
              <ComboboxContent>
                <ComboboxEmpty>No states found.</ComboboxEmpty>
                <ComboboxList>
                  {(state: UsState) => (
                    <ComboboxItem key={state.value} value={state}>
                      {state.label}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </Field>
          <AddressLine
            label={labels.zip_code}
            {...partError("zip_code")}
            required={isRequired}
            inputMode="numeric"
            maxLength={5}
            value={value.zip_code || ""}
            onChange={(e) =>
              update({
                zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
              })
            }
          />
        </div>
        {error && <FieldError id={errorId}>{error}</FieldError>}
      </FieldGroup>
    );
  },
);

AddressInput.displayName = "AddressInput";
